/*! rb-consent-digioh-layer-v1 — Consentmo cookie bar and Digioh popups stay independent.
 *
 * Consentmo paints its bar in the top layer, so it lands on top of an open Digioh offer and
 * covers the offer's own controls. Digioh closes that offer from document-level pointer and
 * touch handlers, so dismissing the cookie bar took the offer down with it.
 *
 * Two halves, both breakpoints:
 *   1. Taps on the consent bar stop at the bar and never reach page-level handlers.
 *   2. While a Digioh modal is up, the bar drops just under its box — behind the offer,
 *      still above the backdrop, so each one is dismissed on its own.
 */
(function () {
  'use strict';

  var HOST_SEL = 'csm-cookie-consent';
  var BAR_SEL = '.csm-cmp-bar';
  var BACKDROP_SEL = '.fb_lightbox-overlay';
  var BOX_SEL = '.preloaded_lightbox';
  var BACKDROP_ID_PREFIX = 'sidebar-overlay-';

  /* Digioh watches pointer, mouse and touch on document; hand.min.js watches touch on body.
     Sealing the whole family keeps every close path off the consent bar. */
  var SEALED_EVENTS = [
    'pointerdown',
    'pointerup',
    'mousedown',
    'mouseup',
    'click',
    'dblclick',
    'touchstart',
    'touchend',
    'touchmove',
    'touchcancel'
  ];

  var syncTimer = 0;

  function stopHere(event) {
    event.stopPropagation();
  }

  function sealConsentBar(host) {
    if (!host || host.rbConsentSealed) return;
    host.rbConsentSealed = true;
    /* Bubble phase: Consentmo's own controls live in the shadow root and have already run
       by the time the event reaches the host, so only page-level listeners are cut off. */
    SEALED_EVENTS.forEach(function (type) {
      host.addEventListener(type, stopHere, false);
    });
  }

  function isOnScreen(el) {
    if (!el) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
    return el.getBoundingClientRect().height > 0;
  }

  function zIndexOf(el) {
    var z = parseInt(window.getComputedStyle(el).zIndex, 10);
    return isNaN(z) ? null : z;
  }

  function boxForBackdrop(backdrop) {
    var id = backdrop.id || '';
    if (id.indexOf(BACKDROP_ID_PREFIX) === 0) {
      var box = document.getElementById(id.slice(BACKDROP_ID_PREFIX.length));
      if (box) return box;
    }
    /* Unnamed backdrop — take the topmost popup that is not the header banner. */
    var best = null;
    var bestZ = -Infinity;
    document.querySelectorAll(BOX_SEL).forEach(function (box) {
      if (box.closest('.rb-header-shell') || !isOnScreen(box)) return;
      var z = zIndexOf(box);
      if (z !== null && z > bestZ) {
        bestZ = z;
        best = box;
      }
    });
    return best;
  }

  /* The layer the consent bar has to slot into: above the backdrop that closes the offer,
     below the offer itself. Digioh leaves a 20 gap between the two. */
  function openModalLayer() {
    var layer = null;
    document.querySelectorAll(BACKDROP_SEL).forEach(function (backdrop) {
      if (layer || !isOnScreen(backdrop)) return;
      var box = boxForBackdrop(backdrop);
      if (!box || !isOnScreen(box)) return;
      var backdropZ = zIndexOf(backdrop);
      var boxZ = zIndexOf(box);
      if (backdropZ === null || boxZ === null || boxZ - backdropZ < 2) return;
      layer = boxZ - 1;
    });
    return layer;
  }

  /* Consentmo shows its host as a `popover`, which puts it in the browser's top layer where
     no z-index can reach it. Out of the top layer the host is still visible on its own
     styles, so the attribute is only dropped while there is an offer to stay behind, then
     handed back so Consentmo keeps working the way it expects. */
  function setTopLayer(host, allowed) {
    if (allowed) {
      if (host.rbConsentPopover && !host.hasAttribute('popover')) {
        host.setAttribute('popover', host.rbConsentPopover);
      }
      return;
    }
    if (!host.hasAttribute('popover')) return;
    host.rbConsentPopover = host.getAttribute('popover');
    host.removeAttribute('popover');
  }

  /* Only the consent bar overlaps an offer. Every other Consentmo surface — the preferences
     dialog reopened from the footer — is left on the vendor's own layer and popover state. */
  function barOnScreen(host) {
    return !!(host.shadowRoot && isOnScreen(host.shadowRoot.querySelector(BAR_SEL)));
  }

  function syncConsentLayer() {
    var host = document.querySelector(HOST_SEL);
    if (!host) return;
    sealConsentBar(host);
    var layer = barOnScreen(host) ? openModalLayer() : null;
    if (layer === null) {
      host.style.removeProperty('z-index');
      setTopLayer(host, true);
      return;
    }
    /* Inline important — Consentmo's own z-index is declared inside its shadow root. */
    host.style.setProperty('z-index', String(layer), 'important');
    setTopLayer(host, false);
  }

  function scheduleSync() {
    if (syncTimer) return;
    syncTimer = window.setTimeout(function () {
      syncTimer = 0;
      syncConsentLayer();
    }, 80);
  }

  function observe() {
    if (typeof MutationObserver === 'undefined') return;
    /* Observe `document`, not documentElement — this file runs in <head>, before <body>. */
    new MutationObserver(scheduleSync).observe(document, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });
  }

  observe();
  scheduleSync();
  document.addEventListener('DOMContentLoaded', scheduleSync);
  window.addEventListener('load', scheduleSync);
  window.addEventListener('resize', scheduleSync, { passive: true });
})();
